import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  AppBskyFeedPost,
  AppBskyFeedLike,
  AppBskyFeedRepost,
  AppBskyGraphFollow,
  AppBskyGraphBlock,
  AppBskyGraphList,
  AppBskyGraphStarterpack,
  AppBskyFeedGenerator,
  AtUri,
} from '@atproto/api'
import { useSettings } from '../store/settings'
import { api, type ActorStatus, type ActorView, type RecordRow } from '../api/client'
import { blobUrl } from '../utils/handle'
import { formatLocal, relativeTime } from '../utils/time'
import { PostCard } from './PostCard'
import { UserCard } from './UserCard'

const PAGE = 200
const ACTOR_BATCH = 25

type SubjectPost = { post?: AppBskyFeedPost.Record; uri: string }

function useHydratedPost(uri: string | null, cid?: string) {
  const service = useSettings((s) => s.service)
  const [data, setData] = useState<SubjectPost | null>(null)
  useEffect(() => {
    if (!uri) return
    let cancelled = false
    const at = new AtUri(uri)
    const u = new URL(`${service}/xrpc/com.atproto.repo.getRecord`)
    u.searchParams.set('repo', at.hostname)
    u.searchParams.set('collection', at.collection)
    u.searchParams.set('rkey', at.rkey)
    if (cid) u.searchParams.set('cid', cid)
    fetch(u.toString())
      .then((r) => (r.ok ? r.json() : null))
      .then((res) => {
        if (cancelled || !res) return
        if (AppBskyFeedPost.isRecord(res.value)) setData({ post: res.value, uri })
        else setData({ uri })
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [uri, cid, service])
  return data
}

type ActorEntry = { status: ActorStatus; view?: ActorView }
const ActorMapContext = createContext<Map<string, ActorEntry>>(new Map())

function useActorEntry(did: string): ActorEntry {
  const map = useContext(ActorMapContext)
  return map.get(did) ?? { status: 'loading' }
}

function HydratedSubjectPost({
  uri,
  cid,
  verb,
  verbedAt,
}: {
  uri: string
  cid?: string
  verb: 'liked' | 'reposted'
  verbedAt: string
}) {
  const data = useHydratedPost(uri, cid)
  if (!data) {
    return (
      <div className="glass flex animate-pulse items-center gap-3 px-4 py-3 text-sm text-ink-300">
        Loading {verb} post…
      </div>
    )
  }
  if (!data.post) {
    return (
      <div className="glass px-4 py-3 text-sm text-ink-300">
        {verb} post unavailable: <span className="font-mono text-xs">{uri}</span>
      </div>
    )
  }
  return <PostCard uri={data.uri} post={data.post} verb={verb} verbedAt={verbedAt} />
}

function HydratedSubjectProfile({
  did,
  verb,
  verbedAt,
}: {
  did: string
  verb: 'followed' | 'blocked'
  verbedAt: string
}) {
  const entry = useActorEntry(did)
  return (
    <UserCard
      did={did}
      handle={entry.view?.handle}
      displayName={entry.view?.displayName}
      description={entry.view?.description}
      avatar={entry.view?.avatar}
      labels={entry.view?.labels?.map((l) => l.val)}
      status={entry.status}
      verb={verb}
      verbedAt={verbedAt}
    />
  )
}

function blobLink(b: unknown): string | null {
  if (!b) return null
  if (typeof b === 'string') return b
  const o = b as { ref?: unknown }
  if (typeof o.ref === 'string') return o.ref
  if (o.ref && typeof o.ref === 'object') {
    const r = o.ref as { $link?: string }
    if (typeof r.$link === 'string') return r.$link
  }
  return null
}

function ListCard({ uri, list }: { uri: string; list: AppBskyGraphList.Record }) {
  const at = new AtUri(uri)
  const did = at.hostname
  const avatarRef = blobLink(list.avatar)
  const purpose = String(list.purpose ?? '').replace('app.bsky.graph.defs#', '')
  const created = list.createdAt ? new Date(list.createdAt) : null
  return (
    <div className="glass flex items-start gap-3 p-4">
      {avatarRef ? (
        <img
          src={blobUrl(did, avatarRef, 'avatar')}
          alt=""
          className="h-12 w-12 flex-shrink-0 rounded-xl object-cover"
        />
      ) : (
        <div className="h-12 w-12 flex-shrink-0 rounded-xl bg-gradient-to-br from-sky-500/40 to-violet-500/40" />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="truncate text-sm font-bold text-white">{list.name}</span>
          <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-widest text-ink-200">
            {purpose}
          </span>
        </div>
        {list.description ? (
          <p className="mt-1 whitespace-pre-wrap text-xs text-ink-100">{list.description}</p>
        ) : null}
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[10px] text-ink-400">
          <span className="font-mono">{at.rkey}</span>
          {created ? <span title={formatLocal(created)}>{relativeTime(created)}</span> : null}
          <a
            href={`https://bsky.app/profile/${did}/lists/${at.rkey}`}
            target="_blank"
            rel="noreferrer"
            className="accent-text hover:underline"
          >
            open list ↗
          </a>
        </div>
      </div>
    </div>
  )
}

function StarterpackCard({
  uri,
  pack,
}: {
  uri: string
  pack: AppBskyGraphStarterpack.Record
}) {
  const at = new AtUri(uri)
  const did = at.hostname
  const created = pack.createdAt ? new Date(pack.createdAt) : null
  return (
    <div className="glass p-4">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-sm font-bold text-white">{pack.name}</span>
        <span className="rounded-full border border-violet-400/30 bg-violet-400/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-violet-200">
          starter pack
        </span>
      </div>
      {pack.description ? (
        <p className="mt-1 whitespace-pre-wrap text-xs text-ink-100">{pack.description}</p>
      ) : null}
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[10px] text-ink-400">
        <span className="font-mono">{at.rkey}</span>
        {created ? <span title={formatLocal(created)}>{relativeTime(created)}</span> : null}
        <a
          href={`https://bsky.app/starter-pack/${did}/${at.rkey}`}
          target="_blank"
          rel="noreferrer"
          className="accent-text hover:underline"
        >
          open pack ↗
        </a>
      </div>
    </div>
  )
}

function FeedGenCard({ uri, feed }: { uri: string; feed: AppBskyFeedGenerator.Record }) {
  const at = new AtUri(uri)
  const did = at.hostname
  const avatarRef = blobLink(feed.avatar)
  const created = feed.createdAt ? new Date(feed.createdAt) : null
  return (
    <div className="glass flex items-start gap-3 p-4">
      {avatarRef ? (
        <img
          src={blobUrl(did, avatarRef, 'avatar')}
          alt=""
          className="h-12 w-12 flex-shrink-0 rounded-xl object-cover"
        />
      ) : (
        <div className="h-12 w-12 flex-shrink-0 rounded-xl bg-gradient-to-br from-emerald-500/40 to-sky-500/40" />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="truncate text-sm font-bold text-white">{feed.displayName}</span>
          <span className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-emerald-200">
            feed generator
          </span>
        </div>
        {feed.description ? (
          <p className="mt-1 whitespace-pre-wrap text-xs text-ink-100">{feed.description}</p>
        ) : null}
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[10px] text-ink-400">
          <span className="font-mono">{at.rkey}</span>
          {created ? <span title={formatLocal(created)}>{relativeTime(created)}</span> : null}
          <a
            href={`https://bsky.app/profile/${did}/feed/${at.rkey}`}
            target="_blank"
            rel="noreferrer"
            className="accent-text hover:underline"
          >
            open feed ↗
          </a>
        </div>
      </div>
    </div>
  )
}

function RowItem({ row }: { row: RecordRow }) {
  const v = row.value as Record<string, unknown>
  if (AppBskyFeedPost.isRecord(v)) {
    return <PostCard uri={row.uri} post={v} />
  }
  if (AppBskyFeedLike.isRecord(v)) {
    return (
      <HydratedSubjectPost uri={v.subject.uri} cid={v.subject.cid} verb="liked" verbedAt={v.createdAt} />
    )
  }
  if (AppBskyFeedRepost.isRecord(v)) {
    return (
      <HydratedSubjectPost uri={v.subject.uri} cid={v.subject.cid} verb="reposted" verbedAt={v.createdAt} />
    )
  }
  if (AppBskyGraphFollow.isRecord(v)) {
    return <HydratedSubjectProfile did={v.subject} verb="followed" verbedAt={v.createdAt} />
  }
  if (AppBskyGraphBlock.isRecord(v)) {
    return <HydratedSubjectProfile did={v.subject} verb="blocked" verbedAt={v.createdAt} />
  }
  if (AppBskyGraphList.isRecord(v)) {
    return <ListCard uri={row.uri} list={v} />
  }
  if (AppBskyGraphStarterpack.isRecord(v)) {
    return <StarterpackCard uri={row.uri} pack={v} />
  }
  if (AppBskyFeedGenerator.isRecord(v)) {
    return <FeedGenCard uri={row.uri} feed={v} />
  }
  return (
    <div className="glass px-4 py-3 text-xs text-ink-300">
      Unknown record type: <span className="font-mono">{(v.$type as string) ?? '—'}</span>
    </div>
  )
}

export type PostFilter = 'all' | 'posts' | 'replies'

function isReplyRecord(v: Record<string, unknown>): boolean {
  if (!AppBskyFeedPost.isRecord(v)) return false
  return !!v.reply?.parent?.uri
}

function extractActorDids(rows: RecordRow[]): string[] {
  const out: string[] = []
  for (const r of rows) {
    const v = r.value
    if (AppBskyGraphFollow.isRecord(v) || AppBskyGraphBlock.isRecord(v)) {
      if (typeof v.subject === 'string') out.push(v.subject)
    }
  }
  return out
}

function useBatchedActors(dids: string[]): Map<string, ActorEntry> {
  const [map, setMap] = useState<Map<string, ActorEntry>>(new Map())
  const requested = useRef<Set<string>>(new Set())

  useEffect(() => {
    // Pick up only the DIDs we've never asked for. Already-requested entries
    // are either resolved (in `map`) or still in flight from an earlier call.
    const toFetch: string[] = []
    for (const did of dids) {
      if (requested.current.has(did)) continue
      requested.current.add(did)
      toFetch.push(did)
    }
    if (toFetch.length === 0) return

    setMap((prev) => {
      const next = new Map(prev)
      for (const did of toFetch) if (!next.has(did)) next.set(did, { status: 'loading' })
      return next
    })

    // Fire and forget — never cancel. Result handling only ADDs entries; even
    // if the component unmounts mid-flight the React state update is a no-op.
    ;(async () => {
      try {
        for (let i = 0; i < toFetch.length; i += ACTOR_BATCH) {
          const slice = toFetch.slice(i, i + ACTOR_BATCH)
          const got = await api.actors(slice)
          setMap((prev) => {
            const next = new Map(prev)
            for (const did of slice) {
              const view = got.get(did)
              next.set(did, view ? { status: 'active', view } : { status: 'unavailable' })
            }
            return next
          })
        }
      } catch {
        // Surface failure as 'unavailable' rather than leaving rows spinning.
        setMap((prev) => {
          const next = new Map(prev)
          for (const did of toFetch) {
            if (next.get(did)?.status === 'loading') next.set(did, { status: 'unavailable' })
          }
          return next
        })
      }
    })()
  }, [dids])

  return map
}

export function RecordList({
  rows,
  postFilter = 'all',
}: {
  rows: RecordRow[]
  postFilter?: PostFilter
}) {
  const [count, setCount] = useState(PAGE)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const filtered = useMemo(() => {
    if (postFilter === 'all') return rows
    return rows.filter((r) => {
      const v = r.value as Record<string, unknown>
      if (!AppBskyFeedPost.isRecord(v)) return true
      return postFilter === 'replies' ? isReplyRecord(v) : !isReplyRecord(v)
    })
  }, [rows, postFilter])

  useEffect(() => {
    setCount(PAGE)
  }, [filtered])

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setCount((c) => Math.min(c + PAGE, filtered.length))
      },
      { rootMargin: '600px' },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [filtered.length])

  const visible = useMemo(() => {
    const sliced = filtered.slice(0, count)
    const seen = new Set<string>()
    const out = []
    for (const r of sliced) {
      if (!r.uri || seen.has(r.uri)) continue
      seen.add(r.uri)
      out.push(r)
    }
    return out
  }, [filtered, count])

  // Batched actor lookup for visible follow/block rows.
  const visibleDids = useMemo(() => extractActorDids(visible), [visible])
  const actorMap = useBatchedActors(visibleDids)

  const hasMore = count < filtered.length

  // Quick stats for follow/block lists
  const actorStats = useMemo(() => {
    const allDids = extractActorDids(filtered)
    if (allDids.length === 0) return null
    let active = 0
    let unavailable = 0
    let loading = 0
    for (const did of allDids) {
      const e = actorMap.get(did)
      if (!e) loading++
      else if (e.status === 'active') active++
      else if (e.status === 'unavailable') unavailable++
      else loading++
    }
    return { total: allDids.length, active, unavailable, loading }
  }, [filtered, actorMap])

  return (
    <ActorMapContext.Provider value={actorMap}>
      <div className="mx-auto mt-4 max-w-6xl px-4 pb-16">
        {actorStats ? (
          <div className="glass mb-3 flex flex-wrap items-center gap-3 px-4 py-2 text-xs text-ink-300">
            <span>
              <strong className="text-white">{actorStats.total.toLocaleString()}</strong> total
            </span>
            <span className="text-ink-400">•</span>
            <span className="text-emerald-200">
              {actorStats.active.toLocaleString()} active
            </span>
            <span className="text-amber-200">
              {actorStats.unavailable.toLocaleString()} unavailable
            </span>
            {actorStats.loading > 0 ? (
              <span className="text-ink-300">
                {actorStats.loading.toLocaleString()} checking…
              </span>
            ) : null}
            <span className="text-ink-400">
              (status only resolves for cards you've scrolled to — keep scrolling for full stats)
            </span>
          </div>
        ) : null}

        <div className="grid gap-3" style={{ contentVisibility: 'auto' }}>
          {visible.map((row) => (
            <RowItem key={row.uri} row={row} />
          ))}
        </div>
        <div ref={sentinelRef} className="mt-6 flex justify-center text-xs text-ink-400">
          {hasMore
            ? `loading more… (${visible.length.toLocaleString()} of ${filtered.length.toLocaleString()})`
            : null}
        </div>
      </div>
    </ActorMapContext.Provider>
  )
}
